import type {
  AmhsLiveInventory,
  AmhsLiveInventoryColumn,
  AmhsLiveInventoryStocker,
} from '../../types/amhsStream';

/** Stocker shelf column reported by the live stream. */
export type LiveStockerColumnKind = 'raw' | 'processed';

function count(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * One stocker column from a live inventory frame. Streams that only send the
 * flat `rawOccupancy` / `processedOccupancy` pair are normalized to the same
 * shape as the explicit `columns` array.
 */
export function liveStockerColumn(
  stocker: AmhsLiveInventoryStocker,
  kind: LiveStockerColumnKind
): AmhsLiveInventoryColumn {
  const explicit = stocker.columns?.find((column) => column.kind === kind);
  if (explicit) return explicit;
  return {
    kind,
    occupancy: count(
      kind === 'raw'
        ? (stocker.rawOccupancy ?? stocker.newOccupancy)
        : stocker.processedOccupancy
    ),
    capacity: count(
      kind === 'raw' ? (stocker.rawCapacity ?? stocker.newCapacity) : stocker.processedCapacity
    ),
  };
}

function normalizeStockerKey(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Live stockers keyed by normalized id, with their position in the inventory
 * frame so lookups can keep resolving ties in inventory order.
 */
export type LiveStockerIndex = Map<string, { stocker: AmhsLiveInventoryStocker; order: number }>;

/**
 * Index a live inventory frame once, so callers resolving many stockers do not
 * re-scan (and re-normalize) the whole frame for each one.
 */
export function buildLiveStockerIndex(
  inventory: AmhsLiveInventory | null | undefined
): LiveStockerIndex {
  const index: LiveStockerIndex = new Map();
  const stockers = inventory?.stockers ?? [];
  for (let order = 0; order < stockers.length; order++) {
    const stocker = stockers[order]!;
    const key = normalizeStockerKey(stocker.stockerId);
    // First frame entry wins, matching a linear scan over inventory order.
    if (key && !index.has(key)) index.set(key, { stocker, order });
  }
  return index;
}

/** Resolve one stocker against a prebuilt index, preferring inventory order. */
export function lookupLiveStocker(
  index: LiveStockerIndex,
  stockerId: string,
  aliases: readonly unknown[] = []
): AmhsLiveInventoryStocker | undefined {
  let best: { stocker: AmhsLiveInventoryStocker; order: number } | undefined;
  const key = normalizeStockerKey(stockerId);
  if (key) best = index.get(key);
  for (const alias of aliases) {
    const aliasKey = normalizeStockerKey(alias);
    if (!aliasKey) continue;
    const hit = index.get(aliasKey);
    if (hit && (!best || hit.order < best.order)) best = hit;
  }
  return best?.stocker;
}

export function findLiveStocker(
  inventory: AmhsLiveInventory | null | undefined,
  stockerId: string,
  aliases: readonly unknown[] = []
): AmhsLiveInventoryStocker | undefined {
  return lookupLiveStocker(buildLiveStockerIndex(inventory), stockerId, aliases);
}

/**
 * Raw / processed occupancy for one stocker, or `undefined` when the stream
 * reports a total but no usable column breakdown — callers then fall back to
 * the undifferentiated total instead of drawing an empty rack.
 */
export function liveStockerColumnSplit(
  stocker: AmhsLiveInventoryStocker | undefined
): { raw: number; processed: number } | undefined {
  if (!stocker) return undefined;
  const raw = count(liveStockerColumn(stocker, 'raw').occupancy);
  const processed = count(liveStockerColumn(stocker, 'processed').occupancy);
  if (raw === 0 && processed === 0 && count(stocker.occupancy) > 0) return undefined;
  return { raw, processed };
}

/**
 * Map rack slot indices to raw/processed FOUP kinds for live playback.
 * Pads or trims column counts so the slot map always matches `rackDisplayCount`
 * (badge / transfer anim can lead held inventory by one frame at LP timing).
 */
export function buildStreamedSlotKinds(
  rackDisplayCount: number,
  columns: { raw: number; processed: number } | undefined,
  totalSlots: number,
  padKind?: LiveStockerColumnKind
): Map<number, LiveStockerColumnKind> | null {
  if (!columns) return null;
  const visibleCount = Math.min(Math.max(0, rackDisplayCount), totalSlots);
  if (visibleCount <= 0) return new Map();

  let rawCount = Math.max(0, columns.raw);
  let processedCount = Math.max(0, columns.processed);
  const total = rawCount + processedCount;

  if (total > visibleCount) {
    let trim = total - visibleCount;
    if (processedCount >= trim) {
      processedCount -= trim;
    } else {
      trim -= processedCount;
      processedCount = 0;
      rawCount = Math.max(0, rawCount - trim);
    }
  } else if (total < visibleCount) {
    const pad = visibleCount - total;
    const kind = padKind ?? (rawCount >= processedCount ? 'raw' : 'processed');
    if (kind === 'raw') rawCount += pad;
    else processedCount += pad;
  }

  const kinds = new Map<number, LiveStockerColumnKind>();
  for (let index = 0; index < rawCount; index += 1) kinds.set(index, 'raw');
  for (let offset = 0; offset < processedCount; offset += 1) {
    kinds.set(rawCount + offset, 'processed');
  }
  return kinds;
}

/** Total held occupancy for one stocker row (respects inventory hold gating). */
export function liveStockerTotalOccupancy(
  inventory: AmhsLiveInventory | null | undefined,
  stockerId: string,
  aliases: readonly unknown[] = []
): number | undefined {
  const stocker = findLiveStocker(inventory, stockerId, aliases);
  if (!stocker) return undefined;
  if (typeof stocker.occupancy === 'number' && Number.isFinite(stocker.occupancy)) {
    return stocker.occupancy;
  }
  const columns = liveStockerColumnSplit(stocker);
  if (columns) return columns.raw + columns.processed;
  return undefined;
}
